import { Skeleton } from '@monorepo/base/src/components/skeleton/skeleton';
import styles from './metrics-cube.module.scss';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { Tooltip } from '@monorepo/controlled/src/components/tooltip/tooltip/tooltip';
import { useDates } from '@monorepo/controlled/src/hooks/use-dates';

export interface IMetricsCube {
	label: string;
	value: string | number | null;
	isLoading?: boolean;
	compareValue?: number | string;
	compareTrend?: MetricCubeTrend;
	valueSum?: string | number | null | undefined;
	compareValueSum?: number | string | undefined;
}

export enum MetricCubeTrend {
	RISING = 'RISING',
	FALLING = 'FALLING',
	EQUAL = 'EQUAL',
}

export default function MetricsCube({ label, value, isLoading, compareTrend, compareValue, valueSum, compareValueSum }: IMetricsCube) {
	const { startDate, endDate } = useDates();
	const formatDate = (date: Date): string =>
		date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: '2-digit',
		});
	const getPreviousDateRange = (startDate: Date, endDate: Date): { prevStart: Date; prevEnd: Date } => {
		const rangeDuration = endDate.getTime() - startDate.getTime();

		const prevEnd = new Date(startDate.getTime());
		const prevStart = new Date(startDate.getTime() - rangeDuration);

		return { prevStart, prevEnd };
	};

	const { prevStart, prevEnd } = getPreviousDateRange(startDate, endDate);

	const formatDelta = (current: string | number | null | undefined, previous: string | number | undefined): string => {
		if (current === null || current === undefined || previous === undefined) {
			return '';
		}

		const toNumber = (val: string | number): number => {
			if (typeof val === 'number') {
				return val;
			}

			let isPercent = false;
			let str = val.trim();

			if (str.includes('%')) {
				isPercent = true;
				str = str.replace('%', '');
			}

			str = str.replace(/[$,]/g, '').replace(/k/i, '000').replace(/m/i, '000000');

			const num = parseFloat(str);
			return isPercent ? num / 100 : num;
		};

		const currNum = toNumber(current);
		const prevNum = toNumber(previous);

		if (isNaN(currNum) || isNaN(prevNum)) {
			return '';
		}

		const wasExplicitPercent =
			(typeof current === 'string' && current.includes('%')) || (typeof previous === 'string' && previous.includes('%'));

		const isLikelyPercent = wasExplicitPercent || (Math.abs(currNum) <= 1 && Math.abs(prevNum) <= 1);

		// Handle percent-like values (e.g. CR from 0.13 to 0.15 = "+2.00%")
		if (isLikelyPercent) {
			const diff = (currNum - prevNum) * 100;
			return diff === 0 ? '' : `${diff >= 0 ? '+' : ''}${diff.toFixed(2)}%`;
		}

		// Avoid division by zero for relative percentage
		if (Math.abs(prevNum) < 0.000001) {
			return '';
		}

		const percentChange = ((currNum - prevNum) / Math.abs(prevNum)) * 100;
		return percentChange === 0 ? '' : `${percentChange >= 0 ? '+' : ''}${percentChange.toFixed(2)}%`;
	};
	const indicatorValue = formatDelta(valueSum, compareValueSum);
	const tooltipContentComparedTo = (
		<span>
			Compared to the equivalent period:
			<br />
			{formatDate(prevStart)} - {formatDate(prevEnd)}
		</span>
	);

	const tooltipContent = indicatorValue ? tooltipContentComparedTo : 'No previous data available';
	return (
		<div className={styles.cubeWrapper}>
			{isLoading ? (
				<div className={styles.skeletonSum}>
					<Skeleton is={isLoading} fullWidth debugProps={{ dataAttrs: [new DataAttribute('id', `metrics_cube_skeleton`)] }} />
				</div>
			) : (
				<Tooltip content={tooltipContent} key={label} is={true}>
					<div className={styles.cube}>
						<div className={styles.label}>{label}</div>
						<div className={styles.valueWrapper}>
							<div className={styles.value}>
								{value}
								{compareValue !== undefined && (
									<span
										className={`${styles.trendInline} ${
											compareTrend === MetricCubeTrend.RISING
												? styles.rising
												: compareTrend === MetricCubeTrend.FALLING
												? styles.falling
												: styles.equal
										}`}>
										&nbsp;{indicatorValue}
									</span>
								)}
							</div>
						</div>
					</div>
				</Tooltip>
			)}
		</div>
	);
}
